import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password?.length >= 8) score++;
  if (/[A-Z]/?.test(password)) score++;
  if (/[0-9]/?.test(password)) score++;
  if (/[^A-Za-z0-9]/?.test(password)) score++;
  const levels = [
    { score: 0, label: '', color: '' },
    { score: 1, label: 'Weak', color: 'bg-red-500' },
    { score: 2, label: 'Fair', color: 'bg-orange-500' },
    { score: 3, label: 'Good', color: 'bg-yellow-500' },
    { score: 4, label: 'Strong', color: 'bg-green-500' },
  ];
  return levels?.[score];
};

const AccountSetupForm = ({ onAccept, onDecline, loading }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const passwordStrength = getPasswordStrength(formData?.password);

  const validateField = (field, value) => {
    switch (field) {
      case 'fullName':
        if (!value?.trim()) return 'Full name is required';
        if (value?.trim()?.length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value?.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/?.test(value)) return 'Include at least one uppercase letter';
        if (!/[0-9]/?.test(value)) return 'Include at least one number';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData?.password) return 'Passwords do not match';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData?.[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
    // Re-validate confirmPassword when password changes
    if (field === 'password' && touched?.confirmPassword) {
      const confirmError = value !== formData?.confirmPassword ? 'Passwords do not match' : '';
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const validateAll = () => {
    const fields = ['fullName', 'password', 'confirmPassword'];
    const newErrors = {};
    fields?.forEach((f) => {
      const err = validateField(f, formData?.[f]);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    setTouched({ fullName: true, password: true, confirmPassword: true });
    return Object.keys(newErrors)?.length === 0;
  };

  const handleAccept = (e) => {
    e?.preventDefault();
    if (!validateAll()) return;
    onAccept({ fullName: formData?.fullName, password: formData?.password });
  };

  const handleDeclineConfirm = () => {
    onDecline(declineReason);
    setShowDeclineDialog(false);
  };

  return (
    <>
      <form onSubmit={handleAccept} className="space-y-5" noValidate>
        {/* Full Name */}
        <div className="space-y-1">
          <Input
            label="Full Name"
            type="text"
            placeholder="e.g. John Smith"
            value={formData?.fullName}
            onChange={(e) => handleChange('fullName', e?.target?.value)}
            onBlur={() => handleBlur('fullName')}
            error={touched?.fullName ? errors?.fullName : ''}
            required
          />
          {touched?.fullName && !errors?.fullName && formData?.fullName && (
            <div className="flex items-center gap-1.5">
              <Icon name="CheckCircle" size={13} className="text-green-600" />
              <span className="text-xs text-green-600">Looks good</span>
            </div>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1">
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a secure password"
              value={formData?.password}
              onChange={(e) => handleChange('password', e?.target?.value)}
              onBlur={() => handleBlur('password')}
              error={touched?.password ? errors?.password : ''}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>
          {/* Strength bar */}
          {formData?.password && (
            <div className="space-y-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4]?.map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all ${
                      i <= passwordStrength?.score ? passwordStrength?.color : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              {passwordStrength?.label && (
                <p className="text-xs text-gray-500">
                  Password strength: <span className="font-medium">{passwordStrength?.label}</span>
                </p>
              )}
            </div>
          )}
          {/* Requirements */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
            {[
              { label: '8+ characters', met: formData?.password?.length >= 8 },
              { label: 'Uppercase letter', met: /[A-Z]/?.test(formData?.password) },
              { label: 'Number', met: /[0-9]/?.test(formData?.password) },
              { label: 'Special character', met: /[^A-Za-z0-9]/?.test(formData?.password) },
            ]?.map(({ label, met }) => (
              <div key={label} className="flex items-center gap-1">
                <Icon
                  name={met ? 'CheckCircle' : 'Circle'}
                  size={11}
                  className={met ? 'text-green-500' : 'text-gray-300'}
                />
                <span className={`text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Confirm Password */}
        <div className="space-y-1">
          <div className="relative">
            <Input
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              placeholder="Re-enter your password"
              value={formData?.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
              onBlur={() => handleBlur('confirmPassword')}
              error={touched?.confirmPassword ? errors?.confirmPassword : ''}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>
          {touched?.confirmPassword && !errors?.confirmPassword && formData?.confirmPassword && (
            <div className="flex items-center gap-1.5">
              <Icon name="CheckCircle" size={13} className="text-green-600" />
              <span className="text-xs text-green-600">Passwords match</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            type="submit"
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-2.5 rounded-lg shadow-sm transition-all"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="Loader2" size={16} className="animate-spin" />
                Setting up account...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Icon name="CheckCircle" size={16} />
                Accept Invitation
              </span>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 border-red-200 text-red-600 hover:bg-red-50 font-medium py-2.5 rounded-lg transition-all"
            onClick={() => setShowDeclineDialog(true)}
            disabled={loading}
          >
            <span className="flex items-center justify-center gap-2">
              <Icon name="XCircle" size={16} />
              Decline Invitation
            </span>
          </Button>
        </div>
      </form>
      {/* Decline Confirmation Dialog */}
      {showDeclineDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Decline Invitation?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to decline this invitation? The invitation will be removed and you will not be able to join this team.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Reason (optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e?.target?.value)}
                placeholder="Let the team know why you're declining..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDeclineDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeclineConfirm}
              >
                Yes, Decline
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSetupForm;
